import React, { useEffect, useState } from 'react';
import { Grid, Skeleton } from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';
import ScenarioCard from './ScenarioCards/ScenarioCards';
import { useNavigate } from 'react-router-dom';
import { LibraryContainer, HeaderGrid, TitleTypography, ControlGrid } from './ScenarioLibrary.Styled';
import { useAppDispatch, useAppSelector } from '../../state/hooks';

import CustomButton from '../../components/UI/CustomButton/CustomButton';
import {
  getOutputOverview,
  resetOverviewData,
  setOutputScreen,
} from '../../features/outputOverview/outputOverviewSlice';
import { fetchOutputFilters } from '../../features/outputFilters/outputfilterSlice';
import DateSelect from './components/DateSelect';
import SearchField from './components/SearchField';
import ShareToUserPopup from './components/ShareToUserPopup';
import ErrorPopup from '../../components/ErrorPopup/ErrorPopup';
import {
  fetchEditScenarioData,
  fetchScenarioLibrary,
  resetFilters,
  setCategoryData,
  setEditScenarioFlag,
  updateDate,
  updateSearchText,
  updateSelectedCategory,
  updateSelectedNumberOfScenarios,
  updateSelectedStatus,
  updateSelectedUser,
} from '../../features/scenarioLibrary/scenarioLibrarySlice';
import { formatCustomDate } from '../../utils/helper';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import { fetchFilterData } from '../../features/filters/filterAPI';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog';
import {
  deleteScenario,
  duplicateScenario,
  setScenarioAction,
  setScenarioName,
} from '../../features/scenarioCreation/scenarioCreationSlice';
import { resetForm } from '../../components/FormComponent/formSlice';
import { toggleEnableReviewTableDialog } from '../ScenarioCreation/components/ReviewTable/reviewTableSlice';
import { DynamicDropdown } from '../../components/UI/DynamicDropdown/DynamicDropdown';
import Swal from 'sweetalert2';

const StatusMapper = {
  Draft: 'draft',
  Completed: 'complete',
};

const UserMapper = {
  Created: 'created',
  Shared: 'shared',
};

const popupMessage = 'More than five scenarios are selected for comparison, please remove before proceeding';

const ScenarioLibrary = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(popupMessage);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const [selectedScenario, setSelectedScenario] = useState({
    scenarioName: '',
    userName: '',
  });
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<null | HTMLElement>(null);
  const {
    user,
    selectedUser,
    status,
    selectedStatus,
    category,
    selectedCategory,
    numberOfScenario,
    selectedNumberOfScenario,
    searchText,
    fromDate,
    toDate,
    scenarios,
    scenariosForComparison,
    error,
    comingFromScreen,
  } = useAppSelector((state) => state.scenarioLibrary);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>(scenariosForComparison);

  const { isOutputScreen } = useAppSelector((state) => state.outputOverview);

  const getCategoryData = async () => {
    const filterResponse = await fetchFilterData({});
    const categoryValues = [
      ...filterResponse.data.enabled_keys.category,
      ...filterResponse.data.disabled_keys.category,
    ];
    dispatch(setCategoryData(categoryValues));
  };

  useEffect(() => {
    getCategoryData();
  }, []);

  const handleSelectScenario = (id: string, selected: boolean) => {
    if (comingFromScreen === 'scenarioCreation') {
      setSelectedScenarios([id]);
    } else {
      setSelectedScenarios((prevSelected) =>
        selected ? [...prevSelected, id] : prevSelected.filter((scenarioId) => scenarioId !== id),
      );
    }
  };

  const handleCalendarOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const confirmScenariosSelection = async () => {
    const selectedScenarioNames = scenarios
      .filter((scenario) => selectedScenarios.includes(scenario.input[0]['scenario_id']))
      .map((scenario) => scenario.scenario_name);
    const totalScenariosSelected = [...selectedScenarioNames];
    if (comingFromScreen === 'scenarioCreation') {
      handleEditScenario(totalScenariosSelected[0]);
    } else {
      if (totalScenariosSelected.length >= 5) {
        setErrorPopupOpen(true);
      } else {
        dispatch(setOutputScreen(false));
        navigate('/scenario-result');
        await dispatch(showLoader());
        await dispatch(resetOverviewData());
        await dispatch(fetchOutputFilters({ scenario_names: totalScenariosSelected }));
        await dispatch(getOutputOverview({ scenarioNames: totalScenariosSelected }));
        await dispatch(hideLoader());
      }
    }
  };

  const sortedData = scenarios;

  // const paginatedData = sortedData.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const paginatedData = sortedData;

  const handleCloseErrorPopup = () => {
    if (comingFromScreen === 'scenarioCreation') {
      navigate('/scenario-creation');
    }
    setErrorPopupOpen(false);
  };

  const handleDateChange = async (start: Date | null, end: Date | null) => {
    const startDate = formatCustomDate(start);
    const endDate = formatCustomDate(end);
    const val = { startDate: startDate, endDate: endDate };
    await dispatch(updateDate([start, end]));
    await fetchScenarioLibraryData(val);
  };

  const handleUserChange = async (newSelected: string[]) => {
    const val = { user: newSelected };
    await dispatch(updateSelectedUser(newSelected));
    await fetchScenarioLibraryData(val);
  };

  const handleCategoryChange = async (newSelected: string[]) => {
    const val = { category: newSelected };
    await dispatch(updateSelectedCategory(newSelected));
    await fetchScenarioLibraryData(val);
  };

  const handleStatusChange = async (newSelected: string[]) => {
    const val = { status: newSelected };
    await dispatch(updateSelectedStatus(newSelected));
    await fetchScenarioLibraryData(val);
  };

  const handleNumberOfScenariosChange = async (newSelected: string[]) => {
    const val = { numberOfScenario: newSelected };
    await dispatch(updateSelectedNumberOfScenarios(newSelected));
    await fetchScenarioLibraryData(val);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(updateSearchText(value));
  };

  const handleSearchScenario = async () => {
    const val = { searchText: searchText };
    await fetchScenarioLibraryData(val);
  };

  const fetchScenarioLibraryData = async (val?: any) => {
    await dispatch(showLoader());
    if (val) {
      const mappedSelectedStatus = selectedStatus.map((status) => StatusMapper[status as keyof typeof StatusMapper]);
      const mappedSelectedUser = selectedUser.map((user) => UserMapper[user as keyof typeof UserMapper]);
      const payload = {
        start_date: val.startDate ? val.startDate : formatCustomDate(fromDate),
        end_date: val.endDate ? val.endDate : formatCustomDate(toDate),
        number_of_scenario: val.numberOfScenario
          ? Number(val.numberOfScenario[0])
          : Number(selectedNumberOfScenario[0]),
        user: val.user ? val.user.map((user: any) => UserMapper[user as keyof typeof UserMapper]) : mappedSelectedUser,
        status: val.status
          ? val.status.map((status: any) => StatusMapper[status as keyof typeof StatusMapper])
          : mappedSelectedStatus,
        category: val.category ? val.category : selectedCategory,
        scenario_name: val.searchText ? val.searchText : searchText,
      };
      await dispatch(fetchScenarioLibrary(payload));
    }
    setOpenDialog(false);
    await dispatch(hideLoader());
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (error !== null) {
      setErrorPopupOpen(true);
      setErrorMessage(error);
    } else {
      setErrorMessage(popupMessage);
      setErrorPopupOpen(false);
    }
  }, [error]);

  const fetchData = () => {
    const val = {
      startDate: formatCustomDate(fromDate),
      endDate: formatCustomDate(toDate),
      numberOfScenario: selectedNumberOfScenario,
      user: selectedUser,
      status: selectedStatus,
      scenario_name: searchText,
    };
    fetchScenarioLibraryData(val);
  };

  const handleDeleteClick = (scenario: string, user: string) => {
    setSelectedScenario({
      scenarioName: scenario,
      userName: user,
    });
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    await dispatch(showLoader());
    const payload = {
      scenario_name: selectedScenario.scenarioName,
      user_name: selectedScenario.userName,
    };
    const response = await dispatch(deleteScenario(payload));
    if (response.payload === 'user deleted for  given scenario') {
      Swal.fire({
        text: 'Scenario succesfully deleted',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#DF2020',
      }).then(async (result) => {
        if (result.isConfirmed) {
          await fetchData();
        }
      });
    }
  };

  const handleEditScenario = async (name: string) => {
    await dispatch(showLoader());
    navigate('/scenario-creation');
    await dispatch(resetForm());
    await dispatch(setEditScenarioFlag(true));
    await dispatch(setScenarioName(name));
    await dispatch(setScenarioAction('edit'));
    await dispatch(fetchEditScenarioData({ scenario_name: name }));
    await dispatch(toggleEnableReviewTableDialog(true));
    await dispatch(hideLoader());
  };

  const handleConfirmUpdateOrRefresh = async (val: string, name: string) => {
    await dispatch(showLoader());
    const payload = {
      scenario_name: name,
    };
    if (val === 'duplicate') {
      // await dispatch(resetFilters());
      await dispatch(duplicateScenario(payload));
      await fetchData();
    } else {
      navigate('/scenario-creation');
      await dispatch(showLoader());
      await dispatch(resetForm());
      await dispatch(setEditScenarioFlag(true));
      await dispatch(setScenarioName(name));
      await dispatch(fetchEditScenarioData(payload));
      await dispatch(setScenarioAction('refresh'));
      await dispatch(toggleEnableReviewTableDialog(true));
      await dispatch(hideLoader());
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Grid style={{ margin: '0 auto' }}>
      <LibraryContainer>
        <HeaderGrid>
          <TitleTypography>Scenario Library</TitleTypography>
          <ControlGrid>
            <DateSelect
              dateRange={[fromDate, toDate]}
              handleDateChange={handleDateChange}
              handleCalendarOpen={handleCalendarOpen}
              handleCalendarClose={handleCalendarClose}
              calendarAnchorEl={calendarAnchorEl}
            />
            <DynamicDropdown
              label="Category"
              options={category}
              selectedOptions={selectedCategory}
              onChange={handleCategoryChange}
              customStyle={{ width: '95px' }}
              multiple={false}
              disabled={isOutputScreen}
              borderColor={'grey'}
            />
            <DynamicDropdown
              label="No of Scenarios"
              options={numberOfScenario}
              selectedOptions={selectedNumberOfScenario}
              onChange={handleNumberOfScenariosChange}
              multiple={false}
              customStyle={{ width: '95px' }}
              borderColor={'grey'}
            />
            <DynamicDropdown
              label="Users"
              options={user}
              selectedOptions={selectedUser}
              onChange={handleUserChange}
              customStyle={{ width: '95px' }}
              borderColor={'grey'}
              disabled={comingFromScreen === 'scenarioCreation'}
            />
            <DynamicDropdown
              label="Status"
              options={status}
              selectedOptions={selectedStatus}
              onChange={handleStatusChange}
              customStyle={{ width: '95px' }}
              borderColor={'grey'}
              disabled={comingFromScreen === 'scenarioCreation'}
            />
            <SearchField value={searchText} onChange={handleSearchInputChange} onSearchClick={handleSearchScenario} />
          </ControlGrid>
        </HeaderGrid>
      </LibraryContainer>
      <LibraryContainer
        style={{
          marginTop: '20px',
          height: '65vh',
          overflowY: 'scroll',
          marginLeft: '0px !important',
        }}
      >
        {scenarios.length === 0 ? (
          <Grid
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid container spacing={6}>
              {[1, 2, 3].map((index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Skeleton variant="rectangular" width={370} height={392} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : (
          <Grid>
            <Grid container spacing={6}>
              {paginatedData.map((element, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <ScenarioCard
                    data={element}
                    selectedScenarios={selectedScenarios}
                    onSelectScenario={handleSelectScenario}
                    scenariosForComparison={scenariosForComparison}
                    handleDelete={handleDeleteClick}
                    handleUpateOrRefresh={handleConfirmUpdateOrRefresh}
                    editScenarios={handleEditScenario}
                  />
                </Grid>
              ))}
            </Grid>
            <Grid
              container
              justifyContent="end"
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                backgroundColor: 'white',
                padding: '10px 20px',
                boxShadow: '0px -2px 5px rgba(0,0,0,0.1)',
                zIndex: 1000,
              }}
            >
              <CustomButton
                style={{
                  color: 'white',
                  backgroundColor: selectedScenarios.length === 0 ? '#7D7D7D' : '#E81C0E',
                  cursor: selectedScenarios.length === 0 ? 'not-allowed' : 'pointer',
                  marginTop: '10px',
                }}
                onClick={confirmScenariosSelection}
                disabled={selectedScenarios.length === 0}
              >
                Confirm Selection
              </CustomButton>
            </Grid>
          </Grid>
        )}
      </LibraryContainer>
      <ShareToUserPopup />
      <ConfirmationDialog open={openDialog} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} />
      <ErrorPopup open={errorPopupOpen} message={errorMessage} onClose={handleCloseErrorPopup} />
    </Grid>
  );
};

export default ScenarioLibrary;
